import React from "react";
import { money } from "../../lib/format";

interface CalculatorWidgetProps {
  title?: string;
  compact?: boolean;
  mode?: "newbie" | "regularRebate";
}

export function sanitizeCalculatorAmount(value: string) {
  const digits = value.replace(/\D/g, "");
  if (!digits) return "";
  return digits.replace(/^0+(?=\d)/, "");
}

export function CalculatorWidget({ title = "新人禮試算器", compact = false, mode = "newbie" }: CalculatorWidgetProps) {
  const inputId = compact ? "calculator-amount-compact" : "calculator-amount";
  const [amountText, setAmountText] = useCalculatorAmount();
  const amount = Number(amountText || 0);
  const isRegularRebate = mode === "regularRebate";
  const coinAmount = amount * 130;
  const fullFirstDeposit = amount * 100;
  const firstDayAmount = fullFirstDeposit * 0.7;
  const stepAmount = fullFirstDeposit * 0.1;
  const secondDeposit = amount * 100 * 0.8;
  const thirdDeposit = amount * 100 * 0.5;
  const regularRate = getRegularRebateRate(amount);
  const regularRewardAmount = amount * 100 * regularRate;
  const formattedAmount = money(amount);

  return (
    <section className={`calculator-widget ${compact ? "compact" : ""}`} aria-label={title} id="calculator-widget">
      <div className="calculator-head">
        <span className="hero-kicker">加碼試算</span>
        <h2>{title}</h2>
        <p>{isRegularRebate ? "輸入單筆充值金額，試算正式成員常態回饋派發金額。" : "輸入入盟啟動金額，快速查看買幣、首存與後續加碼。"}</p>
      </div>

      <label className="calculator-input-block" htmlFor={inputId}>
        <span>{isRegularRebate ? "輸入單筆充值金額" : "輸入入盟啟動金額"}</span>
        <input
          id={inputId}
          type="text"
          inputMode="numeric"
          pattern="[0-9]*"
          placeholder={isRegularRebate ? "請輸入單筆充值金額" : "請輸入入盟啟動金額"}
          value={amountText}
          onChange={(event) => setAmountText(event.target.value)}
          onWheel={(event) => event.currentTarget.blur()}
        />
      </label>

      {isRegularRebate ? (
        <>
          <div className="calculator-highlight-grid">
            <CalcMetric label="充值換算" value={amount * 100} />
            <CalcPercentMetric label="目前級距" value={regularRate ? `${Math.round(regularRate * 100)}%` : "未達"} note="1,000 起算" />
            <CalcMetric label="預計回饋" value={regularRewardAmount} />
          </div>
          <div className="deposit-bonus-panel regular-rebate-tier-list">
            <RebateTier label="1,000 以上" percent="5%" active={amount >= 1000 && amount < 5000} />
            <RebateTier label="5,000 以上" percent="8%" active={amount >= 5000 && amount < 10000} />
            <RebateTier label="10,000 以上" percent="12%" active={amount >= 10000 && amount < 30000} />
            <RebateTier label="30,000 以上" percent="14%" active={amount >= 30000 && amount < 50000} />
            <RebateTier label="50,000 以上" percent="15%" active={amount >= 50000} />
          </div>
        </>
      ) : (
        <>
          <div className="calculator-highlight-grid">
            <CalcMetric label="買幣可得" value={coinAmount} />
            <CalcMetric label="首存完整可領" value={fullFirstDeposit} />
            <CalcMetric label="首日先領" value={firstDayAmount} />
          </div>

          <div className="unlock-axis" aria-label="首存分段解鎖">
            <AxisStep label="首日 70%" value={firstDayAmount} active />
            <AxisStep label="第 2 關 10%" value={stepAmount} />
            <AxisStep label="第 4 關 10%" value={stepAmount} />
            <AxisStep label="第 7 關 10%" value={stepAmount} />
          </div>

          <div className="deposit-bonus-panel">
            <DepositBonus label="二存 80%" note={amount > 0 ? `第二次充值 ${formattedAmount} 可得` : "第二次充值同金額可得"} value={secondDeposit} />
            <DepositBonus label="三存 50%" note={amount > 0 ? `第三次充值 ${formattedAmount} 可得` : "第三次充值同金額可得"} value={thirdDeposit} />
          </div>
        </>
      )}
    </section>
  );
}

function useCalculatorAmount() {
  const state = React.useState("");
  const setAmountText = (value: string) => state[1](sanitizeCalculatorAmount(value));
  return [state[0], setAmountText] as const;
}

function CalcMetric({ label, value }: { label: string; value: number }) {
  return (
    <div className="calc-metric">
      <span>{label}</span>
      <strong>{money(value)}</strong>
      <small>河幣</small>
    </div>
  );
}

function CalcPercentMetric({ label, value, note }: { label: string; value: string; note: string }) {
  return (
    <div className="calc-metric">
      <span>{label}</span>
      <strong>{value}</strong>
      <small>{note}</small>
    </div>
  );
}

function AxisStep({ label, value, active = false }: { label: string; value: number; active?: boolean }) {
  return (
    <div className={`axis-step ${active ? "active" : ""}`}>
      <i />
      <span>{label}</span>
      <strong>{money(value)}</strong>
    </div>
  );
}

function DepositBonus({ label, note, value }: { label: string; note: string; value: number }) {
  return (
    <div>
      <span>{label}</span>
      <small>{note}</small>
      <strong>{money(value)}</strong>
    </div>
  );
}

function RebateTier({ label, percent, active }: { label: string; percent: string; active?: boolean }) {
  return (
    <div className={active ? "active" : ""}>
      <span>{label}</span>
      <small>正式成員常態回饋</small>
      <strong>{percent}</strong>
    </div>
  );
}

function getRegularRebateRate(amount: number) {
  if (amount >= 50000) return 0.15;
  if (amount >= 30000) return 0.14;
  if (amount >= 10000) return 0.12;
  if (amount >= 5000) return 0.08;
  if (amount >= 1000) return 0.05;
  return 0;
}
